<template>
  <div class="availability-filter">
    <div class="availability-filter__form">
      <div class="availability-filter__label">Find available desks:</div>
      <div class="availability-filter__group">
        <AppDateSelect :date="from" class="date-control mr-1 px-0" @change="setDate('from', $event)" />
        <AppTimeSelect :time="from.format('HH:mm')" class="time-control" @change="setTime('from', $event)" />
      </div>
      <span class="mx-2 availability-filter__delimiter">&ndash;</span>
      <div class="availability-filter__group">
        <AppDateSelect :date="to" class="mr-1 ml-0 px-0 date-control" @change="setDate('to', $event)" />
        <AppTimeSelect :time="to.format('HH:mm')" class="time-control" @change="setTime('to', $event)" />
      </div>
      <div class="availability-filter__actions">
        <span
          class="ion ion-md-checkmark close-icon ml-3"
          :class="{ 'opacity-50': !haveChanges }"
          @click="emitChange"
          @touchend.stop.prevent="emitChange"
        ></span>
        <span class="ion ion-md-close close-icon ml-3" @click="close" @touchend.stop.prevent="close"></span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import AppDateSelect from '@/components/shared/AppDateSelect'
import AppTimeSelect from '@/components/shared/AppTimeSelect'
import dayjs from 'dayjs'
import { SERVER_DATE_TIME_FORMAT, timezoneCity, toBuildingDate } from '@/components/desk/utils'

export default {
  name: 'MapAvailabilityFilter',
  components: {
    AppDateSelect,
    AppTimeSelect
  },
  props: {
    floorId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      haveChanges: true,
      // true once the user has picked their own date/time — guards against the floorTimezone
      // watcher clobbering an in-progress edit once floor/building data resolves
      userEditedTime: false,
      from: null,
      to: null
    }
  },
  computed: {
    ...mapGetters('floor', ['getFloor']),
    ...mapGetters('building', ['getBuilding']),
    floorTimezone() {
      const floor = this.floorId ? this.getFloor(this.floorId) : null
      const building = floor ? this.getBuilding(floor.building_id) : null
      return (building && building.timezone) || dayjs.tz.guess()
    },
  },
  methods: {
    setDate(field, date) {
      this.userEditedTime = true
      this[field] = toBuildingDate(date, this.floorTimezone).hour(this[field].hour()).minute(this[field].minute())
      this.haveChanges = true
    },
    setTime(field, time) {
      this.userEditedTime = true
      this[field] = this[field].hour(time.hour).minute(time.minute)
      this.haveChanges = true
    },
    emitChange() {
      const payload = {
        from: this.from.format(SERVER_DATE_TIME_FORMAT),
        to: this.to.format(SERVER_DATE_TIME_FORMAT)
      }
      this.$emit('change', payload)
      this.haveChanges = false
    },
    close() {
      this.$emit('close')
    }
  },
  watch: {
    // floor/building data can still be loading when this filter first mounts (the router doesn't
    // gate on it), so floorTimezone can start out as a browser-guess fallback and only resolve to
    // the real building zone a moment later. Re-anchor the default window once that happens — but
    // only if the user hasn't already picked their own date/time.
    floorTimezone(newTimezone, oldTimezone) {
      if (oldTimezone && !this.userEditedTime) {
        this.from = dayjs().tz(newTimezone).hour(8).minute(0).second(0)
        this.to = dayjs().tz(newTimezone).hour(16).minute(0).second(0)
      }
    }
  },
  created() {
    this.from = dayjs().tz(this.floorTimezone).hour(8).minute(0).second(0)
    this.to = dayjs().tz(this.floorTimezone).hour(16).minute(0).second(0)
  }
}
</script>

<style scoped lang="scss">
.availability-filter {
  background-color: white;
  border-radius: 5px;
  padding: 0.5rem 0.7rem;
  box-shadow: 0 0 0.25rem #0000003f;
 

  @media (max-width: 715px) {
    max-width: 400px;
  }

  &__form {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 0;
    align-items: center;
  }

  &__label {
    margin-right: 0.5rem;
    font-weight: bold;
    @media (max-width: 630px) {
      display: none;
    }
  }

  &__actions {
    text-align: right;
  }

  &__group {
    display: flex;
  }

  &__delimiter {
    font-weight: bold;
    @media (max-width: 630px) {
      display: none;
    }
  }

  &__hint {
    font-size: 11px;
    color: #888;
  }
}

.date-control {
  width: 120px;
}

.time-control {
  width: 66px;
}
</style>
